import React, { useEffect, useMemo, useState } from "react";
import TopBar from "@/components/TopBar";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Save, Trash2, Star, Dumbbell, ExternalLink, Search, Timer,
  Target, AlertTriangle, Wrench, X
} from "lucide-react";

const CATEGORIES = [
  { v: "warmup", l: "Warm-up" },
  { v: "tackling", l: "Tackling" },
  { v: "blocking", l: "Blocking" },
  { v: "footwork", l: "Footwork / Agility" },
  { v: "coverage", l: "Coverage" },
  { v: "throwing", l: "Throwing / Catching" },
  { v: "individual", l: "Individual" },
  { v: "group", l: "Group" },
  { v: "team", l: "Team" },
  { v: "situational", l: "Situational" },
  { v: "conditioning", l: "Conditioning" },
  { v: "special_teams", l: "Special Teams" },
  { v: "general", l: "General" },
];

const DIFFICULTIES = [
  { v: "easy", l: "Easy", tone: "text-emerald-400 border-emerald-500/40" },
  { v: "medium", l: "Medium", tone: "text-amber border-amber/40" },
  { v: "hard", l: "Hard", tone: "text-red-400 border-red-500/40" },
];

const AGE_LEVELS = [
  { v: "youth", l: "Youth" },
  { v: "jv", l: "JV" },
  { v: "varsity", l: "Varsity" },
  { v: "college", l: "College" },
];

function categoryLabel(v) { return (CATEGORIES.find(c => c.v === v) || CATEGORIES[CATEGORIES.length - 1]).l; }
function difficultyMeta(v) { return DIFFICULTIES.find(d => d.v === v) || DIFFICULTIES[1]; }

function DrillCard({ drill, onEdit, onToggleFav, onDelete }) {
  const diff = difficultyMeta(drill.difficulty);
  return (
    <div className="bg-surface border border-surface-3 hover:border-amber/40 p-5 relative group transition-colors" data-testid={`drill-${drill.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[9px] font-condensed uppercase tracking-widest text-amber border border-amber/40 px-1.5 py-0.5">
          {categoryLabel(drill.category)}
        </span>
        <span className={`text-[9px] font-condensed uppercase tracking-widest border px-1.5 py-0.5 ${diff.tone}`}>
          {diff.l}
        </span>
        <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 flex items-center gap-1 ml-auto">
          <Timer className="w-3 h-3" /> {drill.duration_min || 10}m
        </div>
      </div>
      <div className="font-display text-xl uppercase tracking-wide leading-tight text-white mt-2">{drill.name}</div>
      {drill.purpose && (
        <div className="text-sm text-slate-300 mt-2 line-clamp-2">{drill.purpose}</div>
      )}
      {(drill.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {drill.tags.slice(0, 4).map(t => (
            <span key={t} className="text-[9px] font-condensed uppercase tracking-widest text-slate-400 border border-surface-3 px-1.5 py-0.5">{t}</span>
          ))}
        </div>
      )}
      <div className="mt-4 pt-3 border-t border-surface-3 flex items-center justify-between">
        <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">
          {(drill.age_level || "varsity").toUpperCase()}
        </div>
        <div className="flex items-center gap-3">
          {drill.video_url && (
            <a href={drill.video_url} target="_blank" rel="noreferrer" data-testid={`drill-video-${drill.id}`}
              className="text-[10px] font-condensed uppercase tracking-widest text-amber hover:text-amber-400 flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}>
              <ExternalLink className="w-3 h-3" /> Video
            </a>
          )}
          <button onClick={(e) => { e.stopPropagation(); onToggleFav(drill); }}
            data-testid={`drill-fav-${drill.id}`}
            className={`transition-colors ${drill.favorite ? "text-amber" : "text-slate-500 hover:text-amber"}`}>
            <Star className="w-4 h-4" fill={drill.favorite ? "#F59E0B" : "none"} />
          </button>
          <button onClick={() => onEdit(drill)}
            data-testid={`drill-edit-${drill.id}`}
            className="text-[10px] font-condensed uppercase tracking-widest text-slate-400 hover:text-amber">
            Edit
          </button>
          <button onClick={(e) => { e.stopPropagation(); onDelete(drill); }}
            data-testid={`drill-del-${drill.id}`}
            className="text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function DrillEditor({ drill, open, onOpenChange, onSaved }) {
  const [d, setD] = useState(drill);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setD(drill); }, [drill]);

  if (!d) return null;
  const set = (k, v) => setD(prev => ({ ...prev, [k]: v }));

  const save = async () => {
    if (!d.name?.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      const payload = {
        name: d.name, description: d.description || "", purpose: d.purpose || "",
        coaching_points: d.coaching_points || "", common_mistakes: d.common_mistakes || "",
        equipment: d.equipment || "", video_url: d.video_url || "",
        difficulty: d.difficulty || "medium", age_level: d.age_level || "varsity",
        category: d.category || "general", duration_min: +d.duration_min || 10,
        favorite: !!d.favorite,
        tags: typeof d.tags === "string"
          ? d.tags.split(",").map(s => s.trim()).filter(Boolean)
          : (d.tags || []),
      };
      const { data } = d.id ? await api.put(`/drills/${d.id}`, payload) : await api.post("/drills", payload);
      onSaved(data, !!d.id);
      onOpenChange(false);
      toast.success("Drill saved");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail?.message || (typeof detail === "string" ? detail : "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-surface-3 rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="drill-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl uppercase tracking-wide text-white">
            {d.id ? "Edit Drill" : "New Drill"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="col-span-2 md:col-span-4">
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Name</div>
            <Input value={d.name || ""} onChange={(e)=>set("name", e.target.value)}
              className="mt-1 bg-background border-surface-3 rounded-none" data-testid="drill-name" />
          </div>
          <div>
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Category</div>
            <Select value={d.category || "general"} onValueChange={(v)=>set("category", v)}>
              <SelectTrigger className="mt-1 bg-background border-surface-3 rounded-none" data-testid="drill-category"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-surface border-surface-3 rounded-none">
                {CATEGORIES.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Difficulty</div>
            <Select value={d.difficulty || "medium"} onValueChange={(v)=>set("difficulty", v)}>
              <SelectTrigger className="mt-1 bg-background border-surface-3 rounded-none" data-testid="drill-difficulty"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-surface border-surface-3 rounded-none">
                {DIFFICULTIES.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Age Level</div>
            <Select value={d.age_level || "varsity"} onValueChange={(v)=>set("age_level", v)}>
              <SelectTrigger className="mt-1 bg-background border-surface-3 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-surface border-surface-3 rounded-none">
                {AGE_LEVELS.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Duration (min)</div>
            <Input type="number" min={1} value={d.duration_min || 10} onChange={(e)=>set("duration_min", +e.target.value || 10)}
              className="mt-1 bg-background border-surface-3 rounded-none text-center font-mono" data-testid="drill-duration" />
          </div>
          <div className="col-span-2 md:col-span-4">
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Purpose</div>
            <Input value={d.purpose || ""} onChange={(e)=>set("purpose", e.target.value)}
              placeholder="What does this drill teach?"
              className="mt-1 bg-background border-surface-3 rounded-none" data-testid="drill-purpose" />
          </div>
          <div className="col-span-2 md:col-span-4">
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Description / Setup</div>
            <Textarea value={d.description || ""} onChange={(e)=>set("description", e.target.value)} rows={3}
              className="mt-1 bg-background border-surface-3 rounded-none text-sm" data-testid="drill-description" />
          </div>
          <div className="col-span-2 md:col-span-2">
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 flex items-center gap-1">
              <Target className="w-3 h-3" /> Coaching Points
            </div>
            <Textarea value={d.coaching_points || ""} onChange={(e)=>set("coaching_points", e.target.value)} rows={4}
              className="mt-1 bg-background border-surface-3 rounded-none text-sm font-mono" data-testid="drill-coaching-points" />
          </div>
          <div className="col-span-2 md:col-span-2">
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> Common Mistakes
            </div>
            <Textarea value={d.common_mistakes || ""} onChange={(e)=>set("common_mistakes", e.target.value)} rows={4}
              className="mt-1 bg-background border-surface-3 rounded-none text-sm font-mono" />
          </div>
          <div className="col-span-2">
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 flex items-center gap-1">
              <Wrench className="w-3 h-3" /> Equipment
            </div>
            <Input value={d.equipment || ""} onChange={(e)=>set("equipment", e.target.value)}
              className="mt-1 bg-background border-surface-3 rounded-none" />
          </div>
          <div className="col-span-2">
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Video URL (Hudl / YouTube)</div>
            <Input value={d.video_url || ""} onChange={(e)=>set("video_url", e.target.value)}
              placeholder="https://…"
              className="mt-1 bg-background border-surface-3 rounded-none" data-testid="drill-video" />
          </div>
          <div className="col-span-2 md:col-span-4">
            <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">Tags (comma-separated)</div>
            <Input
              value={Array.isArray(d.tags) ? d.tags.join(", ") : (d.tags || "")}
              onChange={(e)=>set("tags", e.target.value)}
              className="mt-1 bg-background border-surface-3 rounded-none" />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button onClick={() => onOpenChange(false)} variant="ghost"
            className="rounded-none h-10 text-[11px] font-condensed uppercase tracking-widest">Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="drill-save"
            className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-10 text-[11px] font-condensed uppercase tracking-widest">
            <Save className="w-3 h-3 mr-1" /> {saving ? "Saving…" : "Save Drill"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function DrillLibrary() {
  const [drills, setDrills] = useState([]);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [filterDiff, setFilterDiff] = useState("all");
  const [onlyFav, setOnlyFav] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try { const { data } = await api.get("/drills"); setDrills(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => drills.filter(d => {
    if (filterCat !== "all" && d.category !== filterCat) return false;
    if (filterDiff !== "all" && d.difficulty !== filterDiff) return false;
    if (onlyFav && !d.favorite) return false;
    if (q) {
      const hay = `${d.name} ${d.purpose} ${d.description} ${d.coaching_points} ${(d.tags||[]).join(" ")}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  }), [drills, filterCat, filterDiff, onlyFav, q]);

  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach(d => { (g[d.category] = g[d.category] || []).push(d); });
    return g;
  }, [filtered]);

  const openNew = () => {
    setEditing({
      id: null, name: "", description: "", purpose: "", coaching_points: "",
      common_mistakes: "", equipment: "", video_url: "", difficulty: "medium",
      age_level: "varsity", category: "general", duration_min: 10, tags: [], favorite: false,
    });
    setEditorOpen(true);
  };

  const openEdit = (d) => { setEditing(d); setEditorOpen(true); };

  const onSaved = (saved, isUpdate) => {
    setDrills(prev => isUpdate ? prev.map(x => x.id === saved.id ? saved : x) : [saved, ...prev]);
  };

  const onToggleFav = async (d) => {
    const payload = {
      name: d.name, description: d.description || "", purpose: d.purpose || "",
      coaching_points: d.coaching_points || "", common_mistakes: d.common_mistakes || "",
      equipment: d.equipment || "", video_url: d.video_url || "",
      difficulty: d.difficulty || "medium", age_level: d.age_level || "varsity",
      category: d.category || "general", duration_min: d.duration_min || 10,
      tags: d.tags || [], favorite: !d.favorite,
    };
    const { data } = await api.put(`/drills/${d.id}`, payload);
    setDrills(drills.map(x => x.id === d.id ? data : x));
  };

  const onDelete = async (d) => {
    if (!window.confirm(`Delete drill "${d.name}"?`)) return;
    await api.delete(`/drills/${d.id}`);
    setDrills(drills.filter(x => x.id !== d.id));
    toast.success("Drill deleted");
  };

  return (
    <div data-testid="drill-library-page">
      <TopBar
        title="Drill Library"
        subtitle={`${drills.length} drill${drills.length !== 1 ? "s" : ""} · drag onto practice periods`}
        actions={
          <Button onClick={openNew} data-testid="new-drill-btn"
            className="rounded-none bg-amber hover:bg-amber-600 text-[#0B0D12] h-10 text-[11px] font-condensed uppercase tracking-[0.25em]">
            <Plus className="w-4 h-4 mr-1" /> New Drill
          </Button>
        }
      />

      <div className="p-8 space-y-6">
        {/* Filters + search */}
        <div className="flex flex-wrap items-center gap-3 bg-surface border border-surface-3 p-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Search drills, coaching points, tags…"
              className="bg-background border-surface-3 rounded-none pl-9" data-testid="drill-search" />
          </div>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-[200px] bg-background border-surface-3 rounded-none h-10" data-testid="drill-filter-cat">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent className="bg-surface border-surface-3 rounded-none">
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterDiff} onValueChange={setFilterDiff}>
            <SelectTrigger className="w-[160px] bg-background border-surface-3 rounded-none h-10" data-testid="drill-filter-diff">
              <SelectValue placeholder="All difficulties" />
            </SelectTrigger>
            <SelectContent className="bg-surface border-surface-3 rounded-none">
              <SelectItem value="all">All difficulties</SelectItem>
              {DIFFICULTIES.map(d => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={() => setOnlyFav(f => !f)} data-testid="drill-only-fav"
            className={`text-[10px] font-condensed uppercase tracking-widest px-3 py-2 border transition-colors flex items-center gap-1 ${
              onlyFav ? "border-amber bg-amber text-[#0B0D12]" : "border-surface-3 text-slate-400 hover:border-amber"
            }`}>
            <Star className="w-3 h-3" fill={onlyFav ? "#0B0D12" : "none"} /> Favorites
          </button>
          {(q || filterCat !== "all" || filterDiff !== "all" || onlyFav) && (
            <button onClick={() => { setQ(""); setFilterCat("all"); setFilterDiff("all"); setOnlyFav(false); }}
              className="text-[10px] font-condensed uppercase tracking-widest text-slate-500 hover:text-amber flex items-center gap-1">
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>

        {loading && <div className="text-sm text-slate-400">Loading drill library…</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-sm text-slate-500 italic border border-dashed border-surface-3 p-12 text-center">
            No drills match. Try clearing filters or add a new drill.
          </div>
        )}

        {Object.keys(grouped).sort().map(cat => (
          <div key={cat} data-testid={`drill-cat-${cat}`}>
            <div className="flex items-center gap-3 mb-3">
              <Dumbbell className="w-4 h-4 text-amber" />
              <div className="font-display text-2xl uppercase tracking-wide text-white">{categoryLabel(cat)}</div>
              <div className="hairline flex-1" />
              <div className="text-[10px] font-condensed uppercase tracking-widest text-slate-500">
                {grouped[cat].length} drill{grouped[cat].length !== 1 ? "s" : ""}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {grouped[cat].map(d => (
                <DrillCard key={d.id} drill={d} onEdit={openEdit} onToggleFav={onToggleFav} onDelete={onDelete} />
              ))}
            </div>
          </div>
        ))}
      </div>

      <DrillEditor drill={editing} open={editorOpen} onOpenChange={setEditorOpen} onSaved={onSaved} />
    </div>
  );
}
